/**
 * CURR-01: display-currency core (KES canonical, USD display-only).
 *
 * Framework-agnostic so both app trees can share one conversion point: the
 * client-side SPA (App.tsx) and the Vike SSR tree (src/pages/**). Nothing here
 * touches React, and every browser-only API is guarded so it is safe to import
 * into a module that renders on the server.
 *
 * Doctrine, and the reason several of these functions look paranoid:
 *   * KES is the canonical currency. USD is a reading aid. It is never stored,
 *     never sent to payments, never entered by an agent, and never the number
 *     a buyer acts on.
 *   * Stale beats wrong. If we cannot prove the rate is current, we show no USD
 *     at all rather than a plausible-looking figure from an unknown date.
 *
 * CURR-03 moved the one impure function — the rate read — to
 * lib/api/exchangeRateApi.ts. It imported ./api/client, which evaluates
 * import.meta.env at module load and therefore dies under plain Node, so the
 * conversion and rounding rules could not be imported by a test at all. Same
 * split, same reason, as lib/api/syndicatedQuery.ts against syndicatedApi.ts:
 * the rules a buyer's price depends on have to be reachable by a test runner.
 */

export type Currency = 'KES' | 'USD';

/** Follows the existing `afriqahome_*` localStorage key convention. */
export const CURRENCY_STORAGE_KEY = 'afriqahome_currency';

export const CURRENCY_PAIR = 'USD_KES';

/**
 * A rate older than this is not shown. The refresh cron runs daily at 04:00
 * UTC, so 48h tolerates one missed run plus slack before we fall back to
 * KES-only. Keep in step with the fallback described on CURR-01.
 */
export const RATE_MAX_AGE_MS = 48 * 60 * 60 * 1000;

/**
 * How often a mounted provider re-checks the rate. Freshness is not a property
 * of the fetch, it is a property of elapsed time, so a long-lived tab or a
 * native app resumed after two days must be able to cross the staleness
 * boundary while mounted rather than trusting whatever it loaded with.
 */
export const RATE_REVALIDATE_INTERVAL_MS = 30 * 60 * 1000;

/**
 * CURR-03 display rounding. At or above this, the figure is compacted to three
 * significant figures with a K/M suffix; below it, to whole hundreds of dollars.
 *
 * The point is not brevity for its own sake — it is that a converted figure
 * should not LOOK more precise than the rate behind it. "≈ $1,546,192" reads
 * like a quote; "≈ $1.55M" reads like what it is, an approximation that moves
 * with the market. It also keeps the line short enough to sit beside a KES
 * price in a phone-width sticky bar without either figure being clipped.
 */
export const USD_COMPACT_THRESHOLD = 10_000;

/** Sub-threshold figures round to whole hundreds: `$8,432` -> `$8,400`. */
export const USD_ROUNDING_STEP = 100;

/**
 * The lowest figure the rounding rule can still render honestly.
 *
 * Anything under half a step rounds to `$0`, which is the single worst thing
 * this feature could put beside a real home — the same reason `convertKesToUsd`
 * has always refused sub-$1 results. Unreachable through the product (the
 * listing floor is KES 100,000, roughly $770), and guarded anyway.
 */
export const USD_DISPLAY_FLOOR = USD_ROUNDING_STEP / 2;

/** Significant figures kept in the compact form: `$1.55M`, `$12.3M`, `$386K`. */
const USD_COMPACT_SIGNIFICANT_DIGITS = 3;

/**
 * CURR-03 copy, in one place so the inline qualifier and the page-level note
 * cannot drift into saying different things about the same figure.
 *
 * Both are bounded by one rule: they may say the figure is approximate and when
 * it was struck, and they may not imply it is held. Nothing on this site quotes
 * a dollar price — prices are set and paid in KES — so "quoted", "locked",
 * "guaranteed" and "fixed" are all wrong, not merely unwise.
 */
export const USD_APPROX_QUALIFIER = 'at today’s rate';
export const USD_APPROX_NOTE = 'Prices are set in KES. USD is approximate.';

/**
 * Largest unit first. `B` is unreachable at today's listing ceiling (KES 200M
 * is about $1.5M) and exists so the ladder can never print `$1550M` if that
 * ceiling ever moves.
 */
const USD_COMPACT_UNITS: ReadonlyArray<{ value: number; suffix: string }> = [
  { value: 1_000_000_000, suffix: 'B' },
  { value: 1_000_000, suffix: 'M' },
  { value: 1_000, suffix: 'K' },
];

export interface ExchangeRate {
  /** KES per 1 USD, e.g. 129.369628. */
  rate: number;
  /** ISO timestamp of the last successful upstream refresh. */
  fetchedAt: string;
}

export function isValidCurrency(value: unknown): value is Currency {
  return value === 'KES' || value === 'USD';
}

/**
 * True only when `fetchedAt` parses and sits inside the freshness window.
 * A future timestamp is treated as fresh — clock skew between the database and
 * the device is not the buyer's problem, and it cannot make the rate wrong.
 */
export function isRateFresh(fetchedAt: string, now: number = Date.now()): boolean {
  const stamp = Date.parse(fetchedAt);
  if (Number.isNaN(stamp)) return false;
  return now - stamp <= RATE_MAX_AGE_MS;
}

/**
 * KES -> whole dollars. No cents on a property price.
 *
 * Returns null when the inputs cannot produce a figure we would show: a
 * non-finite or non-positive price or rate, or a result that rounds to $0.
 * The $0 guard should never fire in practice (listings start around KES 1M)
 * but "$0" next to a real home is the single worst thing this feature could
 * render, so it is refused explicitly rather than assumed impossible.
 */
export function convertKesToUsd(priceKES: number, rate: number): number | null {
  if (!Number.isFinite(priceKES) || priceKES <= 0) return null;
  if (!Number.isFinite(rate) || rate <= 0) return null;

  const usd = Math.round(priceKES / rate);
  if (usd < 1) return null;
  // CURR-03: the display rule rounds to whole hundreds below the compact
  // threshold, which re-creates the "$0" failure mode for anything under $50.
  // Same doctrine as the sub-$1 guard above, applied to what actually renders.
  if (usd < USD_DISPLAY_FLOOR) return null;

  return usd;
}

/**
 * SYND-11: a published foreign-currency amount -> KES, the other direction.
 *
 * `rate` is KES per ONE unit of the published currency, and it is the rate
 * carried by the row itself (`syndicated_public.fx_rate`) — not the app-wide
 * USD_KES rate the buyer's currency toggle uses. Those are two different
 * questions: the toggle asks "how would this buyer like KES presented", and
 * this asks "what did we do to the seller's figure to file it in a KES index".
 * The second has one right answer per row and must not move with a preference.
 *
 * Same refusals as `convertKesToUsd`, for the same reason: a non-finite or
 * non-positive amount or rate produces no figure at all rather than a
 * plausible-looking one. There is no display floor here because the result is
 * compacted, not rounded to hundreds, and the smallest amount that can reach
 * it is a property price.
 *
 * Called from exactly one place — lib/api/syndicatedQuery.ts — and asserted to
 * be, alongside `convertKesToUsd`, in pages/__tests__/currencyDisplay.test.ts.
 * Two conversion paths for one pair is how UX-034's hardcoded 153 survived.
 */
export function convertToKes(amount: number, rate: number): number | null {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (!Number.isFinite(rate) || rate <= 0) return null;

  const kes = Math.round(amount * rate);
  if (kes < 1) return null;

  return kes;
}

/**
 * CURR-03: the exact figure a buyer sees, as a number.
 *
 *   >= $10,000  ->  three significant figures  (1_546_192 -> 1_550_000)
 *   <  $10,000  ->  whole hundreds             (    8_432 ->     8_400)
 *
 * The branch is on the CONVERTED amount, not on the rounded one, which is what
 * makes $9,950 the interesting boundary: it sits below the threshold, so it
 * takes the hundreds rule and lands on exactly 10,000 — printed long, as
 * `$10,000`, while $10,000 itself prints compact as `$10.0K`. Both are the same
 * money; only the notation differs, and pinning which side each falls on is
 * what stops the rule drifting the next time someone touches it.
 */
export function roundUsdForDisplay(usd: number): number {
  if (!Number.isFinite(usd) || usd <= 0) return 0;

  if (usd < USD_COMPACT_THRESHOLD) {
    return Math.round(usd / USD_ROUNDING_STEP) * USD_ROUNDING_STEP;
  }

  const magnitude = Math.floor(Math.log10(usd)) + 1;
  const factor = Math.pow(10, magnitude - USD_COMPACT_SIGNIFICANT_DIGITS);
  return Math.round(usd / factor) * factor;
}

/**
 * CURR-03's compact figure with no currency mark on it: `1.55M`, `386K`,
 * `8,400`.
 *
 * SYND-11 split this out of `formatUsdCompact` rather than writing a second
 * one. The rule it implements is not about dollars — it is about how precise a
 * DERIVED figure is allowed to look next to a published one, and that question
 * is the same whichever way the conversion ran. A syndicated listing published
 * in USD carries a KES approximation, so the same ladder now prints `27.2M`
 * for `KES 27.2M` as it prints `1.55M` for `$1.55M`.
 *
 * The thresholds keep their USD names because that is where the rule was
 * struck and the numbers were chosen there. They are a boundary between
 * "compact" and "long", not an amount of money, and nothing that reaches this
 * function is anywhere near the low end of them: the marketplace floor is
 * KES 100,000 and the lowest indexed foreign price is about $47,000.
 */
export function formatCompactFigure(amount: number): string {
  const rounded = roundUsdForDisplay(amount);

  if (amount < USD_COMPACT_THRESHOLD) {
    return rounded.toLocaleString('en-US');
  }

  // The unit is chosen from the ROUNDED figure, never the raw one, and that
  // ordering is what removes the carry case entirely: $999,950 has already
  // become 1,000,000 by the time a unit is picked, so it lands on M and reads
  // "$1.00M". Picking the unit first would have scaled it to 999.95K and
  // printed "$1000K".
  const found = USD_COMPACT_UNITS.findIndex((unit) => rounded >= unit.value);
  // Unreachable: this branch only runs at $10,000 and above, so `K` always
  // matches. Falling back to the smallest unit rather than the largest keeps a
  // future caller from ever seeing "$0.01B".
  const index = found === -1 ? USD_COMPACT_UNITS.length - 1 : found;
  const scaled = rounded / USD_COMPACT_UNITS[index].value;

  // Three significant figures, wherever the decimal point happens to fall.
  const decimals = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;

  return `${scaled.toFixed(decimals)}${USD_COMPACT_UNITS[index].suffix}`;
}

/**
 * `$1.55M`, `$386K`, `$8,400` — the figure, with no `≈`.
 *
 * The ONE formatter. Every buyer surface that shows USD — detail page, list
 * cards, sticky bar, map popups — reaches this function, most of them through
 * `usdApproxFor` on the currency context. A second formatter is exactly how the
 * hardcoded 153 KES/USD rate from UX-034 survived unnoticed, so there is not
 * one.
 */
export function formatUsdCompact(usd: number): string {
  return `$${formatCompactFigure(usd)}`;
}

/**
 * SYND-11: `KES 27.2M` — a KES figure at the same precision as the USD one.
 *
 * Only ever a DERIVED figure. A KES price the seller actually published is
 * exact and prints in full through `formatSyndicatedPrice` /
 * `formatListingPrice`; nothing published is routed through here, because
 * compacting a real price would throw away digits the seller gave us.
 *
 * The compaction is the point. "≈ KES 27,160,759" reads like a price someone
 * struck, and the give-away is the tail: a figure carried to the shilling is a
 * quote, and this is arithmetic on a rate that moves tomorrow.
 */
export function formatKesCompact(kes: number): string {
  return `KES ${formatCompactFigure(kes)}`;
}

/**
 * `≈ $1.55M`. The `≈` is load-bearing: it is what stops the figure reading as
 * a quoted or locked price.
 */
export function formatUsdApprox(usd: number): string {
  return `≈ ${formatUsdCompact(usd)}`;
}

/** The prefix `formatUsdApprox` adds, kept beside it so a range can strip it. */
const USD_APPROX_PREFIX = /^≈\s*/;

/**
 * `≈ $96.6K – $294K` for a range — or just `≈ $155K` when both ends compact to
 * the same figure.
 *
 * The collapse is not a nicety, it is a correctness fix that CURR-03's rounding
 * made necessary. At three significant figures a KES 20,000,000–20,050,000
 * range converts to $154,596 and $154,982, and BOTH are $155K. Printing
 * "≈ $155K – $155K" announces a range and then denies it in the same breath,
 * which reads as a bug in the price rather than a limit of the approximation.
 *
 * Showing one figure is the honest reading: the KES range above it is exact and
 * still shows the spread; the approximation cannot see a spread that small, and
 * says so by not pretending to.
 *
 * Takes formatted strings rather than numbers because the conversion happens in
 * exactly one place — the currency context — and this must not become a second.
 */
export function formatUsdApproxRange(low: string, high: string): string {
  const lowFigure = low.replace(USD_APPROX_PREFIX, '');
  const highFigure = high.replace(USD_APPROX_PREFIX, '');

  if (lowFigure === highFigure) return low;

  return `${low} – ${highFigure}`;
}

/** Reads the stored preference. Returns null on the server or when unset. */
export function readStoredCurrency(): Currency | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(CURRENCY_STORAGE_KEY);
    return isValidCurrency(stored) ? stored : null;
  } catch {
    // Private mode / disabled storage. A missing preference is not an error.
    return null;
  }
}

export function writeStoredCurrency(currency: Currency): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  } catch {
    // Preference simply does not persist. Never break rendering over it.
  }
}
